/**
 * Class for handling email log REST request
 * @copyright 2021-2022 Soter Technologies, LLC. All rights reserved.
 * @file emailLog.js
 * @author Matt Schreider, Kyle Watkins, Paul Scala
*/

class EmailLog {
    /**
     * Initialize class
     * @param {object} axios Axios instance
     * @param {object} session User session handler
     */
    constructor(axios, session) {
        this.axios = axios
        this.session = session
    }

    /**
     * Get email log
     * @param {object} query Query for filtering
     * @param {number} query.page Page number to get
     * @param {number} query.offset Time offset of page
     * @param {string} query.email Email of user to get logs for
     * @param {string} query.startDate Start date of email log to get
     * @param {string} query.endDate End date of email log to get
     * @param {string} query.deviceId Device id to get email logs for
     * @param {string} query.siteId Site id to get email logs for
     * @param {string} query.userId User id to get email logs for
     * @param {string} query.status Status of email log
     * @param {string} query.order Order of data
     * @param {string} query.sortBy Field to sort by
     * @param {string[]} query.alertType Alert types to filter by
     * @param {boolean} query.countOnly Only get the count of email logs back
     *  @param {'none'|'simple'} query.populate  If set to simple replace all ids to show name and id, default to none which means no ids will be populated in the return
     * @returns {ResponseObject} API response object
     */
    async getEmailLog({page = 0, offset = null, email = null, startDate = null, endDate = null, deviceId = null, siteId = null, userId = null, status = null, alertType = [], order = "ASC", countOnly = false, populate = 'none'} = {}) {
       
        let searchFilters = {
            ...(email !== null && {email: encodeURIComponent(email)}),
            ...(offset !== null && {offset: offset}),
            ...(siteId !== null && {siteId: siteId}),
            ...(userId !== null && {userId: userId}),
            ...(deviceId !== null && {deviceId: deviceId}),
            order: order,
            ...(status !== null && {status: status}),
            ...(startDate !== null && {startDate: startDate}),
            ...(endDate !== null && {endDate: endDate}),
            alertType
        }

        let query = this.session.getQuery({ page, searchFilters, countOnly })
    
        let config = {
            method: 'get',
            url: `/api/emailLog?populate=${populate}${query ? `&${query}` : ''}`,
        }

        let res = await this.axios(config)
        
        return({
            status: res.status,
            statusText: res.statusText,
            isSuccess: (res.status >= 200 && res.status < 300),
            data: res.data
        })
    }
}

export default EmailLog